import React from 'react';
import type { NextPageWithLayout, GetServerSideProps } from 'next';
import { Op, OrderBy } from 'contensis-delivery-api';
import contensis from 'Common/api/contensis';
import cmsParser from 'Common/util/cmsParser';
import setCacheHeaders from 'Common/func/setCacheHeaders';
import { excludeMetadata } from 'Common/util/contensis/api';

import MainLayout from 'Layout/Main';
import Metadata from 'Components/Metadata';
import Template, { datamap, PageData } from 'Templates/Clearing';

const Clearing: NextPageWithLayout<PageData> = ({ entry, courses, ugOpenDay }) => {
    return (
        <>
            <Metadata {...cmsParser.metadata(entry)} />
            <Template {...datamap({ entry, courses, ugOpenDay })} />
        </>
    );
};

Clearing.getLayout = (page, props) => {
    return <MainLayout {...props}>{page}</MainLayout>;
};

export const getServerSideProps: GetServerSideProps<any> = async ({ preview, res }) => {
    const { fetch, surrogateKeys } = contensis({ preview, fetchSurrogateKeys: true });

    const { data, error } = await fetch(async (client, search) => {
        const resPage = await client.nodes.get({
            path: '/clearing',
            entryLinkDepth: 2,
            entryFields: ['*', ...excludeMetadata, '-entryTitle']
        });

        if (!resPage.entry) {
            return { error: true };
        }

        const entry = resPage.entry;

        const coursesQuery = [
            Op.exists('sys.uri', true),
            Op.or(
                Op.equalTo('sys.contentTypeId', 'courseUndergraduate'),
                Op.equalTo('sys.contentTypeId', 'courseFoundationYears')
            )
        ];

        const resCourses = await search(coursesQuery, {
            pageSize: 250,
            fields: ['id', 'degreeType', 'name', '-sys', ...excludeMetadata]
        });

        const eventQuery = [
            Op.or(
                Op.equalTo('sys.contentTypeId', 'eventUndergraduateOpenDay'),
                Op.equalTo('sys.contentTypeId', 'eventClearingOpenDay')
            ),
            Op.greaterThan('date.to', new Date())
        ];

        const resEvents = await search(eventQuery, {
            pageSize: 1,
            fields: ['date', 'location', '-sys'],
            orderBy: OrderBy.asc('date.from')
        });

        return {
            entry,
            courses: resCourses.items,
            ugOpenDay: resEvents.items[0] ? resEvents.items[0] : null
        };
    });

    if (error) {
        return {
            notFound: true
        };
    } else {
        setCacheHeaders(res, surrogateKeys);

        return {
            props: data
        };
    }
};

export default Clearing;
